import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Cable, Network, Wrench, Cpu, CheckCircle2, ArrowRight, Zap, Shield, Clock, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import { usePageTracking } from '../hooks/usePageTracking';

export const InfrastructureFibreOptiquePage = () => {
  usePageTracking();

  const services = [
    {
      id: 'fusion',
      icon: <Cable className="w-8 h-8" />,
      title: 'Fusion de Fibre Optique',
      description: 'Service professionnel de raccordement par fusion de fibres optiques avec équipements de dernière génération.',
      details: [
        {
          title: 'Fusion Monomode & Multimode',
          description: 'Raccordement de fibres monomode et multimode avec des pertes minimales pour garantir une transmission optimale.',
          features: ['Fusion à faible perte', 'Alignement de précision', 'Protection d\'épissures']
        },
        {
          title: 'Tests et Certification',
          description: 'Validation complète des épissures avec équipements de test professionnels (OTDR, réflectomètre).',
          features: ['Tests OTDR', 'Mesure de réflectance', 'Certification de qualité']
        },
        {
          title: 'Documentation Complète',
          description: 'Rapports détaillés de toutes les interventions avec traçabilité complète.',
          features: ['Rapports de fusion', 'Photos des épissures', 'Schémas de câblage']
        }
      ]
    },
    {
      id: 'installation',
      icon: <Network className="w-8 h-8" />,
      title: 'Installation de Réseaux',
      description: 'Construction et installation d\'infrastructures de télécommunications aériennes et souterraines.',
      details: [
        {
          title: 'Installations Aériennes',
          description: 'Pose de câbles sur poteaux avec respect des normes de sécurité et des hauteurs réglementaires.',
          features: ['Câblage sur poteaux', 'Fixations professionnelles', 'Respect des dégagements']
        },
        {
          title: 'Réseaux Souterrains',
          description: 'Installation de conduits, chambres de raccordement et câbles enterrés.',
          features: ['Excavation et pose de conduits', 'Tirage de câbles', 'Chambres et boîtiers']
        },
        {
          title: 'Câblage Structuré',
          description: 'Installation de systèmes de câblage complets pour bâtiments commerciaux et résidentiels.',
          features: ['Backbone fibre optique', 'Distribution horizontale', 'Armoires de brassage']
        }
      ]
    },
    {
      id: 'maintenance',
      icon: <Wrench className="w-8 h-8" />,
      title: 'Entretien & Maintenance',
      description: 'Service complet de maintenance préventive et corrective pour réseaux de fibre optique extérieur.',
      details: [
        {
          title: 'Inspection Régulière',
          description: 'Inspections périodiques pour identifier et prévenir les problèmes avant qu\'ils n\'affectent le service.',
          features: ['Inspection visuelle', 'Tests de performance', 'Détection de dégradation']
        },
        {
          title: 'Réparations Urgentes',
          description: 'Intervention rapide 24/7 en cas de bris ou de panne pour rétablir le service rapidement.',
          features: ['Service d\'urgence 24/7', 'Localisation de pannes', 'Réparation sur site']
        },
        {
          title: 'Maintenance Préventive',
          description: 'Programme de maintenance planifiée pour prolonger la durée de vie de vos infrastructures.',
          features: ['Nettoyage de connecteurs', 'Remplacement préventif', 'Optimisation de performance']
        }
      ]
    },
    {
      id: 'tests',
      icon: <Cpu className="w-8 h-8" />,
      title: 'Tests & Certification',
      description: 'Tests complets et certification de vos infrastructures fibre optique selon les normes de l\'industrie.',
      details: [
        {
          title: 'Tests OTDR',
          description: 'Réflectométrie optique dans le domaine temporel pour analyse complète des liens optiques.',
          features: ['Mesure de longueur', 'Localisation d\'événements', 'Mesure d\'atténuation']
        },
        {
          title: 'Certification de Liens',
          description: 'Certification complète des liens selon les normes TIA/ISO pour garantir la conformité.',
          features: ['Conformité TIA/ISO', 'Tests de bout en bout', 'Rapports certifiés']
        },
        {
          title: 'Analyse de Performance',
          description: 'Évaluation détaillée des performances de vos réseaux pour identifier les opportunités d\'amélioration.',
          features: ['Mesure de bande passante', 'Analyse de perte', 'Recommandations']
        }
      ]
    }
  ];

  const highlights = [
    {
      icon: <Award className="w-6 h-6" />,
      title: 'Expertise Reconnue',
      description: 'Plus de 15 ans d\'expérience dans les infrastructures télécoms'
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: 'Intervention Rapide',
      description: 'Service d\'urgence 24/7 pour vos besoins critiques'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Conformité Garantie',
      description: 'Respect strict des normes et réglementations en vigueur'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Projets Clés en Main',
      description: 'Gestion complète de A à Z avec délais respectés'
    }
  ];

  const advantages = [
    'Techniciens certifiés et expérimentés',
    'Équipements de test de pointe',
    'Documentation détaillée et traçabilité',
    'Respect des normes de sécurité',
    'Garantie sur les travaux effectués',
    'Support technique continu'
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 network-lines opacity-5"></div>
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-500 opacity-20 blur-[150px] rounded-full"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#FF6B35] opacity-20 blur-[150px] rounded-full"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Network className="w-12 h-12 text-[#FF6B35]" />
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B35]">
                Télécommunications
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 tracking-tight">
              Infrastructure <span className="text-[#FF6B35]">Fibre Optique</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-400 max-w-4xl mx-auto">
              Construction, installation, tests et maintenance d&apos;infrastructures de télécommunications aériennes et souterraines. 
              Des solutions professionnelles pour connecter vos installations avec fiabilité et performance.
            </p>
          </motion.div>

          {/* Highlights Grid */}
          <div className="grid md:grid-cols-4 gap-6 mt-12">
            {highlights.map((highlight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-xl p-6 text-center border border-white/10"
              >
                <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mx-auto mb-4 text-[#FF6B35]">
                  {highlight.icon}
                </div>
                <h3 className="font-bold mb-2">{highlight.title}</h3>
                <p className="text-sm text-gray-400">{highlight.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Details */}
      {services.map((service, index) => (
        <section
          key={service.id}
          id={service.id}
          className={`py-20 relative overflow-hidden ${
            index % 2 === 0 ? 'bg-[#050505]' : 'bg-[#0A0A0A]'
          }`}
        >
          <div className="max-w-7xl mx-auto px-6">
            {/* Service Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center text-white shadow-lg">
                  {service.icon}
                </div>
                <div>
                  <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
                    {service.title}
                  </h2>
                  <p className="text-gray-400">{service.description}</p>
                </div>
              </div>
            </motion.div>

            {/* Service Details */}
            <div className="grid md:grid-cols-3 gap-6">
              {service.details.map((detail, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-[#121212] border border-white/10 rounded-xl p-6 hover:border-[#FF6B35]/50 transition-all duration-300 hover:-translate-y-1"
                >
                  <h3 className="text-xl font-bold mb-3">{detail.title}</h3>
                  <p className="text-gray-400 mb-4 text-sm">{detail.description}</p>
                  <div className="space-y-2">
                    {detail.features.map((feature, fIdx) => (
                      <div key={fIdx} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#FF6B35] flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Advantages Section */}
      <section className="py-20 bg-[#0A0A0A] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-black mb-6">
              Pourquoi choisir <span className="text-[#FF6B35]">Innova-Solution</span> ?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {advantages.map((advantage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 bg-[#121212] border border-white/10 rounded-lg p-4 hover:border-[#FF6B35]/50 transition-colors"
              >
                <CheckCircle2 className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
                <span className="text-gray-300">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
        <div className="absolute inset-0 network-lines opacity-5"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-black mb-6">
              Prêt à démarrer votre <span className="text-[#FF6B35]">projet</span> ?
            </h2>
            <p className="text-lg text-gray-400 mb-8">
              Contactez-nous pour discuter de vos besoins en infrastructure fibre optique. Notre équipe d&apos;experts est prête à vous accompagner.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setTimeout(() => {
                    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="bg-[#FF6B35] text-white font-semibold px-8 py-4 rounded-lg hover:bg-[#FF8555] transition-all hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] inline-flex items-center justify-center gap-2"
              >
                Demander une soumission
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="tel:+14503599394"
                className="bg-transparent border-2 border-white/20 text-white font-semibold px-8 py-4 rounded-lg hover:border-white transition-all inline-flex items-center justify-center gap-2"
              >
                (450) 359-9394
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
